import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, CheckCircle2, XCircle, Loader2, Trophy, RotateCcw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';

const uid = () => Math.random().toString(36).slice(2, 10);
const newQuestion = () => ({ id: uid(), text: '', choices: [{ id: uid(), text: '' }, { id: uid(), text: '' }, { id: uid(), text: '' }], correct: [], explanation: '' });
const input = 'w-full rounded-ctrl border border-border bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:border-orange';
const btnPrimary = 'h-10 px-4 rounded-ctrl bg-orange text-orange-on text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50';
const btnGhost = 'h-10 px-4 rounded-ctrl border border-border text-sm text-ink hover:bg-surface-2 transition-colors';

function QuestionEditor({ q, idx, onChange, onRemove }) {
  const set = (patch) => onChange({ ...q, ...patch });
  const setChoice = (cid, text) => set({ choices: q.choices.map(c => (c.id === cid ? { ...c, text } : c)) });
  const toggleCorrect = (cid) => set({ correct: q.correct.includes(cid) ? q.correct.filter(x => x !== cid) : [...q.correct, cid] });
  return (
    <div className="rounded-card border border-border p-4 space-y-2" data-testid={`quiz-edit-q-${idx}`}>
      <div className="flex items-start gap-2">
        <span className="mt-2 text-xs font-bold text-label">Q{idx + 1}</span>
        <input className={input} value={q.text} onChange={e => set({ text: e.target.value })} placeholder="Énoncé de la question" data-testid={`quiz-q-text-${idx}`} />
        <button type="button" onClick={onRemove} className="mt-1 w-8 h-8 rounded-ctrl text-text-3 hover:text-red-600 flex items-center justify-center" title="Supprimer la question" data-testid={`quiz-q-remove-${idx}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      <p className="text-[11px] text-text-3 pl-7">Cochez la ou les bonnes réponses.</p>
      {q.choices.map((c, ci) => (
        <div key={c.id} className="flex items-center gap-2 pl-7">
          <input type="checkbox" checked={q.correct.includes(c.id)} onChange={() => toggleCorrect(c.id)} className="w-4 h-4 accent-[#E8730C]" data-testid={`quiz-q-${idx}-correct-${ci}`} />
          <input className={input} value={c.text} onChange={e => setChoice(c.id, e.target.value)} placeholder={`Réponse ${ci + 1}`} data-testid={`quiz-q-${idx}-choice-${ci}`} />
          {q.choices.length > 2 && <button type="button" onClick={() => set({ choices: q.choices.filter(x => x.id !== c.id), correct: q.correct.filter(x => x !== c.id) })} className="text-text-3 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}
        </div>
      ))}
      <div className="pl-7 flex flex-wrap gap-2 items-center">
        {q.choices.length < 6 && <button type="button" onClick={() => set({ choices: [...q.choices, { id: uid(), text: '' }] })} className="text-xs text-orange-700 underline" data-testid={`quiz-q-add-choice-${idx}`}>+ Réponse</button>}
      </div>
      <input className={`${input} ml-7 w-[calc(100%-1.75rem)]`} value={q.explanation} onChange={e => set({ explanation: e.target.value })} placeholder="Explication affichée après la réponse (optionnel)" />
    </div>
  );
}

export function QuizEditor({ item, onClose, onSaved }) {
  const { api } = useAuth();
  const [qs, setQs] = useState(null);
  const [pass, setPass] = useState(80);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!item) return;
    if (!item.quiz_count) { setQs([newQuestion()]); setPass(80); return; }
    api().get(`/formation/items/${item.id}/quiz`).then(({ data }) => { setQs(data.full.questions); setPass(data.full.pass_score); })
      .catch(() => setQs([newQuestion()]));
  }, [item, api]);
  const save = async (questions) => {
    setBusy(true);
    try {
      const clean = questions.map(q => ({ ...q, choices: q.choices.filter(c => c.text.trim()) }));
      await api().put(`/formation/items/${item.id}/quiz`, { questions: clean, pass_score: Number(pass) || 0 });
      toast.success(clean.length ? `Quiz enregistré (${clean.length} question(s))` : 'Quiz supprimé');
      onSaved(clean.length, Number(pass) || 0);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface sm:w-full max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="quiz-editor">
        <DialogHeader><DialogTitle className="text-ink">Quiz — {item?.titre}</DialogTitle></DialogHeader>
        {!qs ? <Loader2 className="w-5 h-5 animate-spin text-text-3" /> : (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm text-ink">Score pour valider
              <input type="number" min={0} max={100} value={pass} onChange={e => setPass(e.target.value)} className={`${input} w-20`} data-testid="quiz-pass-score" /> %
            </label>
            {qs.map((q, i) => <QuestionEditor key={q.id} q={q} idx={i} onChange={(nq) => setQs(qs.map(x => (x.id === q.id ? nq : x)))} onRemove={() => setQs(qs.filter(x => x.id !== q.id))} />)}
            <button type="button" onClick={() => setQs([...qs, newQuestion()])} className={`${btnGhost} inline-flex items-center gap-2`} data-testid="quiz-add-question"><Plus className="w-4 h-4" /> Ajouter une question</button>
          </div>
        )}
        <DialogFooter className="gap-2">
          {item?.quiz_count > 0 && <button onClick={() => window.confirm('Supprimer ce quiz ?') && save([])} className="h-10 px-4 rounded-ctrl text-sm text-red-600 hover:bg-red-50 mr-auto" data-testid="quiz-delete">Supprimer le quiz</button>}
          <button onClick={onClose} className={btnGhost}>Annuler</button>
          <button onClick={() => save(qs)} disabled={busy || !qs?.length} className={btnPrimary} data-testid="quiz-save">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuizResult({ res, quiz, answers, onRetry }) {
  return (
    <div className="space-y-4" data-testid="quiz-result">
      <div className={`rounded-card p-5 flex items-center gap-4 ${res.passed ? 'bg-emerald-50 border border-emerald-200' : 'bg-orange/5 border border-orange/30'}`}>
        <Trophy className={`w-10 h-10 ${res.passed ? 'text-emerald-600' : 'text-orange'}`} />
        <div>
          <p className="text-2xl font-bold text-ink tabular-nums" data-testid="quiz-score">{res.score} %</p>
          <p className="text-sm text-text-3">{res.good} / {res.total} bonne(s) réponse(s) — {res.passed ? 'Quiz validé, bravo !' : `Il faut ${quiz.pass_score} % pour valider.`}</p>
        </div>
      </div>
      {quiz.questions.map((q, i) => {
        const c = res.corrections.find(x => x.id === q.id);
        return (
          <div key={q.id} className="text-sm">
            <p className="font-semibold text-ink flex items-start gap-2">{c.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />} {i + 1}. {q.text}</p>
            {!c.ok && <p className="pl-6 text-red-700">Votre réponse : {q.choices.filter(ch => (answers[q.id] || []).includes(ch.id)).map(ch => ch.text).join(' · ') || '—'}</p>}
            {!c.ok && <p className="pl-6 text-text-3">Bonne réponse : {q.choices.filter(ch => c.correct.includes(ch.id)).map(ch => ch.text).join(' · ')}</p>}
            {c.explanation && <p className="pl-6 text-text-3 italic">{c.explanation}</p>}
          </div>
        );
      })}
      {!res.passed && <button onClick={onRetry} className={`${btnPrimary}`} data-testid="quiz-retry"><RotateCcw className="w-4 h-4" /> Recommencer</button>}
    </div>
  );
}

export function QuizPlayer({ item, onClose, onDone }) {
  const { api } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!item) return;
    setRes(null); setAnswers({});
    api().get(`/formation/items/${item.id}/quiz`).then(({ data }) => setQuiz(data)).catch(() => toast.error('Quiz indisponible'));
  }, [item, api]);
  const pick = (q, cid) => setAnswers(a => {
    const cur = a[q.id] || [];
    return { ...a, [q.id]: q.multiple ? (cur.includes(cid) ? cur.filter(x => x !== cid) : [...cur, cid]) : [cid] };
  });
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api().post(`/formation/items/${item.id}/quiz/submit`, { answers }); setRes(data); onDone(item.id, data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Envoi impossible'); }
    finally { setBusy(false); }
  };
  const complete = quiz && quiz.questions.every(q => (answers[q.id] || []).length);
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface sm:w-full max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="quiz-player">
        <DialogHeader><DialogTitle className="text-ink">Quiz — {item?.titre}</DialogTitle></DialogHeader>
        {!quiz ? <Loader2 className="w-5 h-5 animate-spin text-text-3" /> : res ? (
          <QuizResult res={res} quiz={quiz} answers={answers} onRetry={() => { setRes(null); setAnswers({}); }} />
        ) : (
          <div className="space-y-5">
            {quiz.questions.map((q, i) => (
              <div key={q.id} className="space-y-2" data-testid={`quiz-question-${i}`}>
                <p className="text-sm font-semibold text-ink">{i + 1}. {q.text} {q.multiple && <span className="font-normal text-text-3">(plusieurs réponses)</span>}</p>
                {q.choices.map((c, ci) => {
                  const on = (answers[q.id] || []).includes(c.id);
                  return (
                    <button key={c.id} type="button" onClick={() => pick(q, c.id)} data-testid={`quiz-answer-${i}-${ci}`}
                      className={`w-full text-left rounded-ctrl border px-3 py-2 text-sm transition-colors ${on ? 'border-orange bg-orange/10 text-ink' : 'border-border text-ink hover:bg-surface-2'}`}>
                      {c.text}
                    </button>
                  );
                })}
              </div>
            ))}
            <button onClick={submit} disabled={!complete || busy} className={btnPrimary} data-testid="quiz-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Valider mes réponses</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
